import React from "react";
import { useState, useEffect } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { toast } from "react-toastify";
import styles from "../Settings.module.scss";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
  getRateCategories,
  getUserRatebyCategory,
  updateUserRates,
  createRateCategory,
  updateRateCategory,
} from "api";

import { Dialog } from "@progress/kendo-react-dialogs";
import AddEdit from "./AddEdit";
import RateCell from "./RateCell";
import SuccessModal from "components/common/Modal/success";
import { useNavigate } from "react-router";
import SubmitButton from "components/common/SubmitButton";

const CategoryInfo = ({ category }) => {
  if (!category) return null;

  return (
    <div
      className="mt-2 d-flex align-items-center gap-2"
      style={{ fontSize: "0.9em" }}
    >
      <span className={styles.categoryInfoText}>
        {category.allowAllApps ? (
          "All Applications"
        ) : (
          <>
            Apps: {category.applications.map((app) => app.appCode).join(", ")}
          </>
        )}
      </span>
      <span className={styles.activeStatusBadge}>
        {category.active ? "Active" : "Inactive"}
      </span>
    </div>
  );
};

const UserRates = (props) => {
  const [ratesData, setRatesData] = useState([]);
  const [SelectedRateValue, setSelectedRateValue] = useState(null);
  const [rateCategories, setRateCategories] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [type, setType] = useState("");
  const [editItem, setEditItem] = useState({ ProductID: 1 });
  const [focusedInputIndex, setFocusedInputIndex] = useState(null);
  const [modalvalue, setmodalvalue] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getCategories();
  }, []);

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  const handleSubmit = async (event) => {
    try {
      if (type === "add") {
        if (
          !event.rateCategoryName ||
          (!event.applications?.length && !event.allowAllApps)
        ) {
          return;
        }
        await createRateCategory(event);
        setmodalvalue(true);
      } else if (type === "edit") {
        if (
          !event.rateCategoryName ||
          (!event.applications?.length && !event.allowAllApps)
        ) {
          return;
        }
        await updateRateCategory(event.rateCategoryId, event);
        setmodalvalue(true);
        setSelectedRateValue(event);
      }
    } catch (error) {} finally {
      setOpenForm(false);
    }
  };

  const getCategories = async () => {
    try {
      const response = await getRateCategories();
      setRateCategories(response?.data?.data);
    } catch (error) {}
  };

  const handleCategoryChange = async (e) => {
    try {
      if (e.value) {
        setSelectedRateValue(e.value);
        const response = await getUserRatebyCategory(e.value?.rateCategoryId);
        const ratesData = response?.data?.data;
        setRatesData(ratesData || []);
      } else {
        setSelectedRateValue(null);
        setRatesData([]);
      }
    } catch (error) {}
  };

  const handleSave = async () => {
    if (!ratesData.length) return;
    setIsSaving(true);
    try {
      await updateUserRates(ratesData[0].rateCategoryId, ratesData);
      toast.success("Rates Updated");
    } catch (error) {} finally {
      setIsSaving(false);
    }
  };

  const itemChange = (event) => {
    const dataIndex = ratesData.findIndex(
      (item) => item.userRef === event.dataItem.userRef
    );
    setFocusedInputIndex(dataIndex);
    const newData = ratesData.map((item) =>
      item.userRef === event.dataItem.userRef
        ? { ...item, rateValue: event.value }
        : item
    );
    setRatesData(newData);
  };

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="d-flex justify-content-between align-items-center mb-3 mx-auto w-100 flex-shrink-0">
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column" style={{ height: "90px" }}>
            <Label className="label mb-2">Rate Category</Label>
            <div>
              <ComboBox
                style={{
                  width: "350px",
                }}
                data={rateCategories}
                iconClassName="wa"
                textField={"rateCategoryName"}
                value={SelectedRateValue}
                onChange={handleCategoryChange}
                placeholder={"Select Category"}
              />
              <CategoryInfo category={SelectedRateValue} />
            </div>
          </div>
        </div>
        <div className="d-flex ms-1 flex-wrap gap-1 gap-xl-3">
          <Button
            className={`common-btn`}
            disabled={!SelectedRateValue}
            onClick={(e) => {
              setEditItem(SelectedRateValue);
              setType("edit");
              setOpenForm(true);
            }}
          >
            Edit Category
          </Button>
          <Button
            className={`common-btn`}
            onClick={(e) => {
              setType("add");
              setOpenForm(true);
            }}
          >
            Add New
          </Button>
        </div>
      </div>
      <div
        className="flex-grow-1 settings-grid users-grid"
        style={{ minHeight: 0, maxWidth: "1200px", margin: "0 auto", width: "100%" }}
      >
        <Grid
          data={ratesData}
          style={{ height: "100%" }}
          sortable={true}
          onItemChange={itemChange}
          dataItemKey={"userRef"}
        >
          <Column field="userRef" title="User Ref" width="220px" />
          <Column field="displayName" title="Name" />
          <Column
            field="rateValue"
            title="Rate"
            width={150}
            cell={(props) => (
              <RateCell
                {...props}
                isFocused={focusedInputIndex === props.dataIndex}
              />
            )}
            style="float: right"
          />
        </Grid>
      </div>
      <div className={`${styles["buttons-container"]} d-flex align-items-center justify-content-end gap-2 px-4 py-3`}>
        <Button
          className="common-btn bgBtnSecondary"
          onClick={() => navigate("/settings")}
        >
          Cancel
        </Button>
        <SubmitButton
          onClick={handleSave}
          isLoading={isSaving}
          disabled={!ratesData.length || isSaving}
        >
          Save
        </SubmitButton>
      </div>

      {modalvalue && (
        <SuccessModal
          onClose={() => {
            getCategories();
            setmodalvalue(false);
          }}
          message={type !== "add" ? "Category Updated" : "Category Added"}
        />
      )}

      {openForm && (
        <Dialog
          onClose={handleCancelEdit}
          width={600}
          title={type === "add" ? "Add Rate Category" : "Edit Rate Category"}
        >
          <div className="company">
            <AddEdit
              cancelEdit={handleCancelEdit}
              onSubmit={handleSubmit}
              item={editItem}
              type={type}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default UserRates;
